import { PAGES } from "@/api/types/studio";

/**
 * Pure validation for the Studio composer's send state — kept out of the
 * component so it can be unit-tested without rendering.
 *
 * Rules (contract: body 1..8000 unless attachments are present; feedback
 * carries a site page):
 * - a message needs a non-empty body or at least one uploaded attachment
 *   (photo-only sends post an empty body);
 * - the body stays within the 8000-char cap;
 * - feedback mode requires a page chosen from the contract's page list;
 * - a send is blocked while any attachment is still uploading.
 */
export const MAX_BODY_LENGTH = 8000;

/** Contract cap: attachment_ids holds at most 40 ids per message. */
export const MAX_ATTACHMENTS = 40;

export interface ComposerState {
  body: string;
  kind: "chat" | "feedback";
  page: string | null;
  uploading: boolean;
  /** Uploaded attachments that will be sent (failed uploads do not count). */
  attachmentCount: number;
}

/** First thing wrong with the composer state, or null when it can send. */
export function composerValidationError(state: ComposerState): string | null {
  const body = state.body.trim();
  if (state.uploading) return "Wait for attachments to finish uploading";
  if (state.attachmentCount > MAX_ATTACHMENTS) {
    return `Too many photos (max ${MAX_ATTACHMENTS} per message)`;
  }
  if (!body && state.attachmentCount === 0) return "Message is empty";
  if (body.length > MAX_BODY_LENGTH) {
    return `Message is too long (max ${MAX_BODY_LENGTH} characters)`;
  }
  if (state.kind === "feedback") {
    if (!state.page) return "Pick the site page this feedback is about";
    if (!PAGES.some((p) => p.path === state.page)) {
      return `Unknown page: ${state.page}`;
    }
  }
  return null;
}

export function canSend(state: ComposerState): boolean {
  return composerValidationError(state) === null;
}
